import { useCallback, useEffect, useMemo, useState } from "react";

import { useToast } from "@/providers/ToastProvider";

import {
  callIntercom,
  isIntercomBlocked,
  isMessengerPanelOpen,
  whenIntercomReady,
} from "./intercom-sdk";
import { isMessengerOpen, onMessengerShown } from "./messenger-state";

const SUPPORT_EMAIL = "hello@weld.app";

const OPEN_VERIFY_DELAY_MS = 2000;

/**
 * How long a click waits for the widget before giving up and saying chat is
 * unavailable. Shorter than the default, because someone who just clicked is
 * owed an answer — but long enough that a slow load is not called an ad
 * blocker.
 */
const CLICK_READY_TIMEOUT_MS = 4000;

/**
 * Reactive variant of isIntercomBlocked, for the warning affordance in the
 * sidebar.
 *
 * Waits for the widget rather than sampling once on a timer: index.html only
 * requests the script on window `load`, which on a heavy page routinely lands
 * seconds in, and a single early sample would leave the sidebar accusing an ad
 * blocker for the rest of the session over a merely slow load.
 */
export function useIsIntercomBlocked(): boolean {
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    if (!isIntercomBlocked()) {
      return;
    }
    return whenIntercomReady(() => setBlocked(isIntercomBlocked()));
  }, []);
  return blocked;
}

/** Opening and closing the messenger, with a fallback when it will not open. */
export function useIntercom() {
  const toast = useToast();

  const showAdblockerWarning = useCallback(() => {
    toast(
      "Chat unavailable",
      <span>
        Our chat widget couldn't load — it may be blocked by an ad blocker or
        privacy extension. Try pausing it for this site, or email us at{" "}
        <a
          className="underline"
          href={`mailto:${SUPPORT_EMAIL}`}
          rel="noreferrer"
        >
          {SUPPORT_EMAIL}
        </a>
        .
      </span>,
      "warning",
      8000,
    );
  }, [toast]);

  return useMemo(() => {
    const tryShow = (run: () => void) => {
      // A click can land before window `load` has even requested the script,
      // when the stub is all there is. Warning then would blame an ad blocker
      // for a page that is merely still loading, so wait for the widget first
      // and only call it blocked if it never turns up.
      whenIntercomReady(() => {
        showWhenReady(run);
      }, CLICK_READY_TIMEOUT_MS);
    };

    const showWhenReady = (run: () => void) => {
      if (isIntercomBlocked()) {
        showAdblockerWarning();
        return;
      }
      // The DOM check is not redundant: a panel Intercom restored on load is
      // up without having fired `onShow`, so the flag reads false. Verifying in
      // that state would warn that chat is unavailable while it is plainly
      // open, because show() on an open messenger does not re-fire onShow.
      if (isMessengerOpen() || isMessengerPanelOpen()) {
        run();
        return;
      }
      // `onShow` is the only reliable confirmation that the panel opened: a
      // shut-down messenger swallows `show()` without error and leaves every
      // Intercom element in the DOM, so watching for those cannot tell the two
      // apart.
      let opened = false;
      const unsubscribe = onMessengerShown(() => {
        opened = true;
      });
      run();
      setTimeout(() => {
        unsubscribe();
        if (!opened) {
          showAdblockerWarning();
        }
      }, OPEN_VERIFY_DELAY_MS);
    };

    return {
      show: () => {
        tryShow(() => callIntercom("show"));
      },
      hide: () => {
        callIntercom("hide");
      },
      openChat: (prefilledMessage?: string) => {
        tryShow(() => callIntercom("showNewMessage", prefilledMessage));
      },
      startChecklist: (checklistId: number) => {
        callIntercom("startChecklist", checklistId);
      },
    };
  }, [showAdblockerWarning]);
}
